import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import Marquee from "@/components/Marquee";
import LevelChart from "@/components/LevelChart";
import LeadForm from "@/components/LeadForm";
import AskAndrogen from "@/components/AskAndrogen";
import Faq from "@/components/Faq";
import { MaskedLine, Reveal, Label, SectionHead, EASE } from "@/components/ui-bits";
import { BRAND, MARQUEE_ITEMS, HOW_IT_WORKS, SAFETY_PILLARS, FAQS } from "@/data/site";
import { TIERS, ONE_OFF_FEES } from "@/data/pricing";
import { FEATURED_ARTICLES } from "@/data/articles";
import { ArticleCard } from "@/pages/LearnHub";

const IMG = {
  consult:
    "https://images.unsplash.com/photo-1758691463198-dc663b8a64e4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHxkb2N0b3IlMjBjb25zdWx0YXRpb24lMjBjbGluaWN8ZW58MHx8fHwxNzkxNzQzMzcxfDA&ixlib=rb-4.1.0&q=85",
  lab: "https://images.unsplash.com/photo-1606206591513-adbfbdd7a177?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwyfHxibG9vZCUyMHRlc3QlMjBsYWJvcmF0b3J5JTIwc2NpZW5jZXxlbnwwfHx8fDE3OTE3NDMzNzF8MA&ixlib=rb-4.1.0&q=85",
};

function Hero() {
  return (
    <section className="relative pt-36 sm:pt-44 pb-16 sm:pb-24 px-5 sm:px-8 overflow-hidden" data-testid="hero-section">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-40 right-[-10%] w-[720px] h-[720px] rounded-full opacity-30"
        style={{ background: "radial-gradient(circle, rgba(0,229,192,0.16) 0%, rgba(0,229,192,0.04) 45%, transparent 70%)" }}
      />
      <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 lg:gap-10 items-center relative">
        <div className="lg:col-span-7">
          <Reveal y={12}>
            <Label>Androgen Health — clinical care, online</Label>
          </Reveal>
          <h1 className="font-display font-semibold tracking-tight leading-[0.92] text-[13.5vw] sm:text-7xl lg:text-[5.6rem] mt-7">
            <MaskedLine delay={0.15}>Testosterone care,</MaskedLine>
            <MaskedLine delay={0.3}>
              <span className="serif-it text-teal text-glow pr-2">guided by</span>
            </MaskedLine>
            <MaskedLine delay={0.45}>clinical evidence.</MaskedLine>
          </h1>
          <Reveal delay={0.75}>
            <p className="text-mute text-base sm:text-lg leading-relaxed mt-8 max-w-xl">
              Understand your symptoms. Get the right investigations. Receive a clear clinical
              assessment and ongoing care if treatment is appropriate.
            </p>
          </Reveal>
          <Reveal delay={0.9}>
            <div className="flex flex-wrap gap-4 mt-10">
              <Link to="/book" className="btn-primary" data-testid="hero-book-assessment-cta">
                Book an assessment
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M5 12h14m-6-6 6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Link>
              <Link to="/learn/adam-questionnaire" className="btn-ghost" data-testid="hero-check-symptoms-cta">
                2-minute symptom check
              </Link>
            </div>
          </Reveal>
          <Reveal delay={1.05}>
            <p className="mono-label text-dusk mt-10 max-w-md leading-loose">{BRAND.safety}</p>
          </Reveal>
        </div>

        <div className="lg:col-span-5">
          <Reveal delay={0.5} y={40}>
            <LevelChart />
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Bento() {
  return (
    <section className="py-20 sm:py-28 px-5 sm:px-8" data-testid="bento-section">
      <div className="max-w-7xl mx-auto">
        <SectionHead
          label="The clinic, in brief"
          title={
            <>
              Not another subscription.
              <br />
              <span className="serif-it text-teal">A specialist service.</span>
            </>
          }
        />
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 mt-14">
          <Reveal className="md:col-span-8" delay={0.05}>
            <div className="card-line rounded-2xl p-8 sm:p-10 h-full flex flex-col justify-between min-h-[300px]">
              <p className="mono-label text-teal">{BRAND.positioning}</p>
              <div>
                <h3 className="font-display text-3xl sm:text-4xl tracking-wide leading-tight mt-6 max-w-xl">
                  We built Androgen Health because testosterone clinics earned the wrong reputation —
                  aggressive marketing, loose diagnosis, treatment as a product.
                </h3>
                <p className="text-mute leading-relaxed mt-6 max-w-xl">
                  Our answer is unglamorous: assess properly, price transparently, monitor for the
                  long term, and say plainly when treatment is not the answer.
                </p>
                <Link to="/why-androgen-health" className="inline-flex items-center gap-2 text-teal text-sm mt-8 link-underline" data-testid="bento-why-link">
                  Why Androgen Health
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 12h14m-6-6 6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
                </Link>
              </div>
            </div>
          </Reveal>
          <Reveal className="md:col-span-4" delay={0.12}>
            <div className="card-line rounded-2xl overflow-hidden h-full min-h-[300px] relative group">
              <img
                src={IMG.consult}
                alt="A clinician in consultation, reviewing results with a patient"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-[1.04]"
                style={{ filter: "grayscale(0.55) contrast(1.05) brightness(0.75)" }}
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent" />
              <p className="absolute bottom-6 left-6 mono-label text-bone/90">SPECIALIST-LED ASSESSMENT</p>
            </div>
          </Reveal>
          <Reveal className="md:col-span-4" delay={0.16}>
            <div className="card-line rounded-2xl p-7 h-full">
              <p className="font-display text-6xl text-teal">£99</p>
              <p className="mono-label text-dusk mt-2">FROM · PER MONTH</p>
              <p className="text-mute text-sm leading-relaxed mt-4">
                Foundation plan, proposed launch pricing. Full first-year costs published before you
                commit.
              </p>
            </div>
          </Reveal>
          <Reveal className="md:col-span-4" delay={0.2}>
            <div className="card-line rounded-2xl overflow-hidden h-full min-h-[240px] relative group">
              <img
                src={IMG.lab}
                alt="Blood sample tubes in a laboratory"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-[1.04]"
                style={{ filter: "grayscale(0.55) contrast(1.05) brightness(0.75)" }}
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent" />
              <p className="absolute bottom-6 left-6 mono-label text-bone/90">PROPER BLOOD TESTING</p>
            </div>
          </Reveal>
          <Reveal className="md:col-span-4" delay={0.24}>
            <div className="card-line rounded-2xl p-7 h-full">
              <p className="font-display text-6xl text-teal">0</p>
              <p className="mono-label text-dusk mt-2">PRESSURE PRESCRIBING</p>
              <p className="text-mute text-sm leading-relaxed mt-4">
                Advised against treatment when it isn't indicated — including on the patients we
                would otherwise bill.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section className="py-20 sm:py-28 px-5 sm:px-8 border-t border-line" data-testid="how-it-works-section">
      <div className="max-w-7xl mx-auto">
        <SectionHead label="How it works" title="A simple clinical journey" lede="Four steps from first concerns to long-term care — each one clinically decided, never sales-decided." />
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-14">
          {HOW_IT_WORKS.map((s, i) => (
            <Reveal key={s.step} delay={i * 0.08} className="h-full">
              <div className="card-line rounded-2xl p-7 h-full group hover:border-teal/30 transition-colors duration-500">
                <p className="font-mono text-teal text-sm tracking-widest">{s.step}</p>
                <h3 className="font-display text-2xl tracking-wide mt-4">{s.title}</h3>
                <p className="text-mute text-sm leading-relaxed mt-3">{s.copy}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function PricingPreview() {
  return (
    <section className="py-20 sm:py-28 px-5 sm:px-8 border-t border-line" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHead label="Transparent fees" title="Three plans. Every safety measure included." lede="Plans differ in service level and monitoring — never in whether essential safety measures are provided. Proposed launch pricing." />
          <Reveal delay={0.2}>
            <div className="flex flex-wrap gap-3">
              <Link to="/trt-cost#compare" className="btn-primary !py-3 !px-6 text-sm" data-testid="pricing-compare-plans-link">
                Compare plans side by side
              </Link>
              <Link to="/trt-cost" className="btn-ghost !py-3 !px-6 text-sm" data-testid="pricing-view-fees-link">
                Full fees & terms
              </Link>
            </div>
          </Reveal>
        </div>
        <div className="grid lg:grid-cols-3 gap-5 mt-14">
          {TIERS.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.08} className="h-full">
              <div
                className={`card-line rounded-2xl p-8 h-full flex flex-col relative transition-colors duration-500 ${
                  t.popular ? "border-teal/40" : ""
                }`}
                data-testid={`pricing-tier-${t.name.toLowerCase()}`}
              >
                {t.popular && (
                  <span className="absolute -top-3 left-8 px-3 py-1 rounded-full bg-teal text-ink text-xs font-semibold z-10" data-testid={`pricing-tier-${t.name.toLowerCase()}-badge`}>
                    Most popular plan
                  </span>
                )}
                <div className="relative -mx-8 -mt-8 mb-6 aspect-[16/10] overflow-hidden rounded-t-2xl">
                  <img src={t.image} alt={t.imageAlt} loading="lazy" className="absolute inset-0 w-full h-full object-cover" data-testid={`pricing-tier-${t.name.toLowerCase()}-image`} />
                  <div className="absolute inset-0 bg-gradient-to-t from-surface via-transparent to-transparent" />
                </div>
                <p className="mono-label text-dusk">{t.badge}</p>
                <h3 className="font-display text-3xl tracking-wide mt-3">{t.name}</h3>
                <p className="text-bone/80 text-sm mt-1">{t.form}</p>
                <p className="mt-4">
                  <span className="font-display text-5xl text-teal">{t.price}</span>
                  <span className="text-dusk text-sm ml-2">{t.period}</span>
                </p>
                <p className="text-mute text-sm leading-relaxed mt-4">{t.copy}</p>
                <dl className="mt-5 grid grid-cols-[auto,1fr] gap-x-4 gap-y-2 text-sm border-y border-line py-4" data-testid={`pricing-tier-${t.name.toLowerCase()}-specs`}>
                  <dt className="mono-label text-dusk">Feature</dt>
                  <dd className="text-bone/85">{t.feature}</dd>
                  <dt className="mono-label text-dusk">Monitoring</dt>
                  <dd className="text-bone/85">{t.monitoring}</dd>
                </dl>
                <ul className="mt-5 space-y-2.5 flex-1">
                  {t.features.map((f) => (
                    <li key={f} className="flex gap-2.5 text-sm text-bone/80">
                      <span className={`mt-0.5 ${f.startsWith("Everything") ? "text-teal font-semibold" : "text-teal"}`}>{f.startsWith("Everything") ? "＋" : "·"}</span>
                      {f}
                    </li>
                  ))}
                </ul>
                <Link to="/book" className="btn-ghost justify-center mt-8 text-sm" data-testid={`pricing-tier-${t.name.toLowerCase()}-select`}>
                  Assess for {t.name}
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <p className="text-xs text-dusk mt-8 leading-relaxed max-w-3xl">
            Foundation and Precision launch first; Specialist follows once demand and clinician
            capacity are proven. No safety-critical test or review the clinician deems necessary is
            ever charged as an extra.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function Safety() {
  return (
    <section className="py-20 sm:py-28 px-5 sm:px-8 border-t border-line" data-testid="safety-section">
      <div className="max-w-7xl mx-auto">
        <SectionHead
          label="Clinical safety"
          title={
            <>
              The risks, in plain English.
              <br />
              <span className="serif-it text-teal">Before you decide anything.</span>
            </>
          }
          lede="TRT can suppress fertility, raises haematocrit and requires prostate-related monitoring where appropriate. A service that doesn't lead with this isn't a clinic."
        />
        <div className="grid md:grid-cols-2 gap-5 mt-14">
          {SAFETY_PILLARS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.07} className="h-full">
              <div className="card-line rounded-2xl p-8 h-full">
                <h3 className="font-display text-2xl tracking-wide">{p.title}</h3>
                <p className="text-mute text-sm leading-relaxed mt-3">{p.copy}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <Link to="/safety" className="btn-ghost mt-10 !py-3 !px-6 text-sm" data-testid="safety-read-more-link">
            Read the full safety standard
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function AskSection() {
  return (
    <section className="py-20 sm:py-28 px-5 sm:px-8 border-t border-line" data-testid="ask-section">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-4">
          <SectionHead
            label="AI health library — beta"
            title={
              <>
                Ask Androgen.
                <br />
                <span className="serif-it text-teal">Get evidence, not pressure.</span>
              </>
            }
            lede="An AI assistant trained on careful guardrails: educational answers about testosterone health, no diagnosis, no prescribing — and an honest nudge towards proper assessment."
          />
          <Reveal delay={0.2}>
            <p className="text-xs text-dusk leading-relaxed mt-6">
              Choose an engine: Claude Sonnet 5.5 or GPT-5.4. Answers stream live and are never a
              substitute for clinical advice.
            </p>
          </Reveal>
        </div>
        <div className="lg:col-span-8">
          <AskAndrogen />
        </div>
      </div>
    </section>
  );
}

function LearnTeaser() {
  return (
    <section className="py-20 sm:py-28 px-5 sm:px-8 border-t border-line" data-testid="learn-teaser-section">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHead label="Knowledge centre" title="Learn about TRT — properly." lede="Evidence-based guides written for patients, reviewed for launch, and honest about uncertainty." />
          <Reveal delay={0.2}>
            <Link to="/learn" className="btn-ghost !py-3 !px-6 text-sm" data-testid="learn-view-all-link">
              All guides
            </Link>
          </Reveal>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-14">
          {FEATURED_ARTICLES.slice(0, 4).map((a, i) => (
            <ArticleCard key={a.slug} a={a} delay={i * 0.07} testIdPrefix="learn-teaser" />
          ))}
        </div>
      </div>
    </section>
  );
}

function FaqSection() {
  return (
    <section className="py-20 sm:py-28 px-5 sm:px-8 border-t border-line" data-testid="faq-section">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <SectionHead label="FAQ" title="Asked, answered." lede="The questions patients actually ask — answered the same way our clinicians answer them." />
        </div>
        <div className="lg:col-span-8">
          <Faq items={FAQS} />
        </div>
      </div>
    </section>
  );
}

function Assessment() {
  return (
    <section id="assessment" className="py-20 sm:py-28 px-5 sm:px-8 border-t border-line scroll-mt-24" data-testid="assessment-section">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-start">
        <div>
          <SectionHead
            label="Start your assessment"
            title={
              <>
                Explore your
                <br />
                assessment options.
              </>
            }
            lede="Tell us a little about your situation. We'll come back with the right assessment pathway — screening, comprehensive testing or a full consultation — and its exact cost, before anything is booked."
          />
          <Reveal delay={0.2}>
            <ul className="mt-8 space-y-3">
              {["No treatment promised — assessment first", "No payment taken at enquiry", "Your details used only to respond to you"].map((t) => (
                <li key={t} className="flex items-center gap-3 text-sm text-bone/80">
                  <span className="w-1.5 h-1.5 rounded-full bg-teal" /> {t}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <LeadForm />
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Seo
        title="Androgen Health | UK Specialist TRT Clinic — Testosterone Care, Online"
        description="Specialist online TRT clinic. Evidence-led assessment, transparent pricing from £99 a month and ongoing monitoring. Treatment is subject to individual clinical assessment."
      />
      <Hero />
      <Marquee items={MARQUEE_ITEMS} />
      <Bento />
      <HowItWorks />
      <PricingPreview />
      <Safety />
      <AskSection />
      <LearnTeaser />
      <FaqSection />
      <Assessment />
    </>
  );
}
